import React, {useCallback, useEffect, useMemo, useState} from 'react';
import FrontloadingPhase from './FrontloadingPhase';
import LCVProgressTracker from './LCVProgressTracker';
import LCVStageTable from './LCVStageTable';
import LCVTutorDialog from './LCVTutorDialog';
import LCVTopBar from './LCVTopBar';
import LCVViewBoard from './LCVViewBoard';

const DISCLAIMER_KEY = 'lcv-frontloading-disclaimer-dismissed';
const ENABLE_FRONTLOADING_LISTENING = false;

const LCVFrontloadingStage = ({
  data,
  metadata,
  progressData,
  phase,
  trackerMode,
  visualContent,
  onStart,
  paused = false,
}) => {
  const termCount = data?.terms?.length || 0;
  const [activeIndex, setActiveIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [fontSize, setFontSize] = useState(17);
  const [dialogRunId, setDialogRunId] = useState(0);
  const [showDisclaimer, setShowDisclaimer] = useState(() => {
    try {
      return !sessionStorage.getItem(DISCLAIMER_KEY);
    } catch {
      return true;
    }
  });

  useEffect(() => {
    setActiveIndex(0);
    setFinished(!ENABLE_FRONTLOADING_LISTENING);
    setDialogRunId(0);
  }, [data?.topic, termCount]);

  const handleDismissDisclaimer = useCallback(() => {
    setShowDisclaimer(false);
    try {
      sessionStorage.setItem(DISCLAIMER_KEY, '1');
    } catch {
      // noop
    }
  }, []);

  const handleRestart = useCallback(() => {
    setActiveIndex(0);
    setFinished(!ENABLE_FRONTLOADING_LISTENING);
    setDialogRunId(current => current + 1);
  }, []);

  const handleTutorFinished = useCallback(() => {
    if (termCount > 0) {
      setActiveIndex(termCount - 1);
    }
    setFinished(true);
  }, [termCount]);

  const handleTutorProgress = useCallback(
    ({spokenWords, totalWords}) => {
      if (finished || termCount <= 1 || totalWords <= 0) return;

      const wordsPerCard = totalWords / termCount;
      const nextIndex = Math.min(
        termCount - 1,
        Math.floor(spokenWords / Math.max(wordsPerCard, 1)),
      );

      setActiveIndex(nextIndex);
    },
    [finished, termCount],
  );

  const tutorLabel = useMemo(() => {
    if (termCount === 0) return '';
    return `${Math.min(activeIndex + 1, termCount)} of ${termCount}`;
  }, [activeIndex, termCount]);

  if (!data || !progressData) return null;

  return (
    <LCVStageTable
      className="lcv-stage-table-frontloading"
      topLeft={<LCVTopBar metadata={metadata} />}
      topCenter={<LCVProgressTracker data={progressData} phase={phase} mode={trackerMode} />}
      topRight={<div className="lcv-stage-empty" />}
      middleLeft={
        visualContent ? (
          <LCVViewBoard phase={phase} visualContent={visualContent} />
        ) : (
          <div className="lcv-stage-empty" />
        )
      }
      middleCenter={
        <div className="lcv-stage-middle-shell lcv-stage-middle-shell-frontloading">
          <FrontloadingPhase
            data={data}
            activeIndex={activeIndex}
            finished={finished}
            onStart={onStart}
            onRestart={handleRestart}
            showDisclaimer={showDisclaimer}
            onDismissDisclaimer={handleDismissDisclaimer}
            listeningEnabled={ENABLE_FRONTLOADING_LISTENING}
          />
        </div>
      }
      middleRight={<div className="lcv-stage-empty" />}
      bottomLeft={<div className="lcv-stage-empty" />}
      bottom={
        <LCVTutorDialog
          key={`frontloading-dialog-${dialogRunId}`}
          text={data.tutorScript}
          label={tutorLabel}
          animated={ENABLE_FRONTLOADING_LISTENING}
          paused={paused}
          onFinished={handleTutorFinished}
          speed={speed}
          fontSize={fontSize}
          showControls={ENABLE_FRONTLOADING_LISTENING}
          onSpeedChange={setSpeed}
          onFontSizeChange={setFontSize}
          onRestart={handleRestart}
          onProgress={handleTutorProgress}
        />
      }
    />
  );
};

export default LCVFrontloadingStage;
